import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Download,
  FileSpreadsheet,
  FileText,
  RefreshCw,
  Search,
  X,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface ExportFile {
  id: number;
  file_type: 'excel' | 'pdf';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  file_name: string | null;
  file_size: number | null;
  total_records: number | null;
  download_url: string | null;
  error_message: string | null;
  created_at: string;
  expires_at: string;
  progress?: number;
  client?: {
    id: number;
    trade_name: string;
    legal_name: string;
  };
}

interface ReportsCenterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ReportsCenter: React.FC<ReportsCenterProps> = ({ open, onOpenChange }) => {
  const { toast } = useToast();
  const [files, setFiles] = useState<ExportFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
  });
  const [fileProgress, setFileProgress] = useState<Record<number, number>>({});

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.listExportFiles({
        limit: pagination.limit,
        offset: pagination.offset,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        file_type: fileTypeFilter !== 'all' ? fileTypeFilter : undefined,
      });

      if (response.success) {
        const filesList = response.data.files || [];
        setFiles(filesList);
        setPagination((prev) => ({
          ...prev,
          total: response.data.total || 0,
        }));
        
        // Carregar progresso para arquivos em processamento
        const processingFiles = filesList.filter((f: ExportFile) => 
          f.status === 'processing' || f.status === 'pending'
        );
        
        if (processingFiles.length > 0) {
          processingFiles.forEach(async (file: ExportFile) => {
            try {
              const progressResponse = await api.getExportProgress(file.id);
              if (progressResponse.success && progressResponse.data) {
                setFileProgress((prev) => ({
                  ...prev,
                  [file.id]: progressResponse.data.progress || 0,
                }));
              }
            } catch (error) {
              // Ignorar erros ao buscar progresso
            }
          });
        }
      }
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao carregar arquivos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.offset, statusFilter, fileTypeFilter, toast]);

  useEffect(() => {
    if (open) {
      loadFiles();
    }
  }, [open, loadFiles]);

  // Atualizar automaticamente a cada 5 segundos quando o modal estiver aberto
  // E atualizar progresso a cada 2 segundos para arquivos em processamento
  useEffect(() => {
    if (!open) return;
    
    const interval = setInterval(() => {
      loadFiles();
    }, 5000);
    
    // Atualizar progresso para arquivos em processamento
    const progressInterval = setInterval(async () => {
      // Buscar arquivos em processamento da lista atual
      const currentFiles = files.filter((f) => 
        f.status === 'processing' || f.status === 'pending'
      );
      
      if (currentFiles.length > 0) {
        await Promise.all(
          currentFiles.map(async (file) => {
            try {
              const progressResponse = await api.getExportProgress(file.id);
              if (progressResponse.success && progressResponse.data) {
                const progress = progressResponse.data.progress || 0;
                setFileProgress((prev) => ({
                  ...prev,
                  [file.id]: progress,
                }));
                
                // Se completou ou falhou, recarregar lista de arquivos
                if (progress === 100 || progressResponse.data.state === 'completed' || progressResponse.data.state === 'failed') {
                  setTimeout(() => loadFiles(), 1000);
                }
              }
            } catch (error) {
              // Ignorar erros
            }
          })
        );
      }
    }, 2000); // Atualizar progresso a cada 2 segundos

    return () => {
      clearInterval(interval);
      clearInterval(progressInterval);
    };
  }, [open, loadFiles, files]);

  const handleDownload = async (file: ExportFile) => {
    try {
      if (file.download_url) {
        const link = document.createElement('a');
        link.href = file.download_url;
        link.setAttribute('download', file.file_name || 'arquivo.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const response = await api.downloadExportFile(file.id);
        if (response.success && response.data.download_url) {
          const link = document.createElement('a');
          link.href = response.data.download_url;
          link.setAttribute('download', file.file_name || 'arquivo.xlsx');
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      }

      toast({
        title: 'Download iniciado',
        description: 'O arquivo está sendo baixado...',
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao baixar arquivo',
        variant: 'destructive',
      });
    }
  };

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Concluído
          </Badge>
        );
      case 'processing':
        return (
          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Processando
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Pendente
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            Falhou
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const filteredFiles = files.filter((file) => {
    const matchesSearch =
      !searchTerm ||
      file.file_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.client?.trade_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-primary" />
            Central de Relatórios
          </DialogTitle>
          <DialogDescription>
            Gerencie e baixe todos os seus relatórios exportados
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Filtros e Busca */}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex-1 flex gap-2 items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por nome do arquivo ou cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setSearchTerm('')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="flex gap-2 items-center">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                  <SelectItem value="processing">Processando</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="failed">Falhou</SelectItem>
                </SelectContent>
              </Select>

              <Select value={fileTypeFilter} onValueChange={setFileTypeFilter}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="icon"
                onClick={loadFiles}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Tabela de Arquivos */}
          <div className="flex-1 overflow-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Data de Criação</TableHead>
                  <TableHead className="font-semibold">Nome do Arquivo</TableHead>
                  <TableHead className="font-semibold">Tipo</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Tamanho</TableHead>
                  <TableHead className="font-semibold">Registros</TableHead>
                  <TableHead className="font-semibold">Cliente</TableHead>
                  <TableHead className="font-semibold text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && files.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-primary" />
                      <p className="text-gray-500">Carregando arquivos...</p>
                    </TableCell>
                  </TableRow>
                ) : filteredFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <AlertCircle className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-500">
                        {searchTerm ? 'Nenhum arquivo encontrado com os filtros aplicados' : 'Nenhum arquivo encontrado'}
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFiles.map((file) => (
                    <TableRow key={file.id} className="hover:bg-gray-50">
                      <TableCell className="text-sm">
                        {format(new Date(file.created_at), "dd/MM/yyyy 'às' HH:mm", {
                          locale: ptBR,
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {file.file_type === 'excel' ? (
                            <FileSpreadsheet className="w-4 h-4 text-green-600" />
                          ) : (
                            <FileText className="w-4 h-4 text-red-600" />
                          )}
                          <span className="font-medium">
                            {file.file_name || 'Nome não disponível'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="uppercase">
                          {file.file_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          {getStatusBadge(file.status)}
                          {(file.status === 'processing' || file.status === 'pending') && (
                            <div className="w-full space-y-1">
                              <Progress 
                                value={fileProgress[file.id] || 0} 
                                className="h-2 w-full"
                              />
                              <p className="text-xs text-gray-500">
                                {fileProgress[file.id] || 0}% concluído
                              </p>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatFileSize(file.file_size)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {file.total_records !== null
                          ? file.total_records.toLocaleString('pt-BR')
                          : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {file.client?.trade_name || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {file.status === 'completed' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(file)}
                            className="gap-2"
                          >
                            <Download className="w-4 h-4" />
                            Baixar
                          </Button>
                        ) : file.status === 'failed' && file.error_message ? (
                          <div className="flex items-center gap-2 text-xs text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            <span className="max-w-[200px] truncate" title={file.error_message}>
                              {file.error_message}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Aguarde...</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t pt-4">
              <div className="text-sm text-gray-600">
                Mostrando {pagination.offset + 1} até{' '}
                {Math.min(pagination.offset + pagination.limit, pagination.total)} de{' '}
                {pagination.total} arquivos
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      offset: Math.max(0, prev.offset - prev.limit),
                    }))
                  }
                  disabled={pagination.offset === 0}
                >
                  Anterior
                </Button>
                <div className="flex items-center gap-1 px-4">
                  <span className="text-sm">
                    Página {currentPage} de {totalPages}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      offset: prev.offset + prev.limit,
                    }))
                  }
                  disabled={currentPage >= totalPages}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportsCenter;
